import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../i18n/LanguageContext';
import { FaPlay, FaClock } from 'react-icons/fa';
import { useDynamicTranslation } from '../../../Utils/translateDynamicData';

const AllLiveStreams = ({ liveStreams, loading, error }) => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { translateLiveStreamData } = useDynamicTranslation(language);

  const handleWatchVideo = (stream) => {
    navigate(`/live/${stream.videoId}`);
  };



  if (loading) {
    return (
      <section className="py-8 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="loader mx-auto mb-4"></div>
            <p className="text-gray-600">{t('loading')}</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-8 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Live Streams</h2>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </section>
    );
  }

  if (!liveStreams || liveStreams.length === 0) {
    return (
      <section className="py-8 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <FaClock className="text-6xl text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('noLiveVideos')}
            </h2>
            <p className="text-gray-600">
              No live videos at the moment. Check back later for live spiritual sessions and ceremonies.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 bg-gradient-to-br from-orange-50 to-amber-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-orange-800 mb-4">
            🔴 {t('allLiveStreams') || 'All Live Streams'}
          </h2>
          <p className="text-gray-600">
            {liveStreams.length} {t('liveStreamsAvailable') || 'live streams available'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {liveStreams.map((stream, index) => {
            const translatedStream = translateLiveStreamData(stream, language);
            
            return (
              <div key={stream.videoId || index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group">
                <div className="relative">
                  <div className="aspect-w-16 aspect-h-9 bg-black">
                    <img 
                      src={translatedStream.thumbnail} 
                      alt={translatedStream.title} 
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-30"></div>
                    
                    {/* Live Indicator */}
                    <div className="absolute top-3 left-3 bg-red-600 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center space-x-1">
                      <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                      <span>{t('live')}</span>
                    </div>
                    
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <button 
                        onClick={() => handleWatchVideo(stream)}
                        className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center hover:from-orange-600 hover:to-red-600 transition-all transform hover:scale-110 shadow-2xl opacity-0 group-hover:opacity-100"
                      >
                        <FaPlay className="text-white text-xl ml-1" />
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                    {translatedStream.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">
                    {translatedStream.channelTitle}
                  </p>
                  {translatedStream.description && (
                    <p className="text-xs text-gray-500 line-clamp-2">
                      {translatedStream.description}
                    </p>
                  )}
                  
                  <button 
                    onClick={() => handleWatchVideo(stream)}
                    className="w-full mt-3 bg-gradient-to-r from-orange-500 to-red-500 text-white py-2 px-4 rounded-lg font-semibold hover:from-orange-600 hover:to-red-600 transition-all flex items-center justify-center space-x-2"
                  >
                    <FaPlay className="text-sm" />
                    <span>{t('watchNow') || 'Watch Now'}</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AllLiveStreams;
